import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  softphoneStickyKey,
  useAvailableCallerIds,
  useSaveSoftphoneSticky,
  useSoftphoneSticky,
} from '@/api/agents'
import { useCallActions } from '@/hooks/useCallActions'
import { useProfile } from '@/api/profile'
import {
  AUTO_CALLER_ID,
  normalizePhone,
  resolveAutoCallerId,
} from '@/lib/callerIdAuto'
import { readSessionCallerId, saveSessionCallerId } from '@/lib/sessionCallerId'

/**
 * Numéro présenté et départ d'un appel depuis le clavier.
 *
 * Sorti de PhoneTab pour servir aussi le clavier de la Boîte de réception
 * (bouton « Composer un numéro »). Comportement inchangé : numéro étoilé par
 * défaut, mode Automatique (sticky → indicatif → étoile), sticky mémorisé.
 */
export function useDialer() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: loadedCallerIds } = useAvailableCallerIds()
  const availableCallerIds = loadedCallerIds ?? []
  const { makeOutboundCall } = useCallActions()
  const { data: profile } = useProfile()
  const qc = useQueryClient()
  const saveSticky = useSaveSoftphoneSticky()
  const preferredCallerId = profile?.preferred_caller_id || null

  // Numéro en cours de composition (remonté par le DialPad) — sert à
  // prévisualiser le caller ID résolu en mode Automatique.
  const [composedNumber, setComposedNumber] = useState('')

  // Défaut = numéro étoilé (préféré) ; jamais le « main » de la company.
  // Seulement des lignes de l'entreprise : une ligne d'un autre compte resté
  // dans l'onglet est oubliée (readSessionCallerId).
  const preferredIsLine = availableCallerIds.some((l) => l.phone_number === preferredCallerId)
  const selectedCallerId =
    readSessionCallerId(loadedCallerIds) ||
    (preferredIsLine ? preferredCallerId : null) ||
    availableCallerIds[0]?.phone_number ||
    null

  const isAutoCallerId = selectedCallerId === AUTO_CALLER_ID

  // Sticky du contact en cours de composition (mémoire backend, par agent).
  // On ne l'interroge qu'en mode auto et sur un numéro complet.
  const normalizedComposed = normalizePhone(composedNumber)
  const stickyQuery = useSoftphoneSticky(
    isAutoCallerId && normalizedComposed.length >= 12 ? normalizedComposed : null,
  )

  // Résout le caller ID effectif pour un numéro donné. En Automatique :
  // sticky (backend, si dispo dans le cache) → match indicatif → numéro étoilé.
  const resolveCallerIdFor = (dialed: string): string | undefined => {
    if (!isAutoCallerId) return selectedCallerId || undefined
    const sticky =
      qc.getQueryData<string | null>(
        softphoneStickyKey(normalizePhone(dialed)),
      ) ?? null
    return (
      resolveAutoCallerId(dialed, availableCallerIds, {
        preferredCallerId,
        stickyCallerId: sticky,
      }).number || undefined
    )
  }

  // Place un appel en résolvant le caller ID, et mémorise le numéro présenté
  // pour ce contact quand on est en mode Automatique (sticky).
  const placeCall = (dialed: string) => {
    const callerId = resolveCallerIdFor(dialed)
    void makeOutboundCall(dialed, callerId)
    if (isAutoCallerId && callerId) {
      const key = normalizePhone(dialed)
      if (key) saveSticky.mutate({ contact_phone: key, caller_id: callerId })
    }
  }

  // Aperçu du numéro que l'Automatique utiliserait pour le numéro composé,
  // + d'où il vient (sticky / match / étoile) pour l'afficher à l'agent.
  const autoResolution = isAutoCallerId
    ? resolveAutoCallerId(composedNumber, availableCallerIds, {
        preferredCallerId,
        stickyCallerId: stickyQuery.data ?? null,
      })
    : null

  const handleSelectCallerId = (num: string) => {
    saveSessionCallerId(num)
    const params = new URLSearchParams(searchParams)
    params.set('cid', num.slice(-4))
    setSearchParams(params, { replace: true })
  }

  return {
    availableCallerIds,
    selectedCallerId,
    handleSelectCallerId,
    placeCall,
    setComposedNumber,
    autoResolvedNumber: autoResolution?.number ?? null,
    autoResolvedSource: autoResolution?.source ?? null,
  }
}
